import {OtsDateTime} from "../components/OtsDateTime";
import {AppAccessPackage} from "@ots-frontend/ots-data";
import {StatusPill} from "../components/StatusPill";
import {appAccessCardClass, getAppAccessApp, getAppAccessIdentitySummary, getAppAccessPackageDeliveryStatus} from "./app-access-ui";

export type AppAccessPackageListProps = {
  packages: AppAccessPackage[];
  selectedId?: string;
  isLoading?: boolean;
  onSelect: (appAccessPackage: AppAccessPackage) => void;
  className?: string;
  title?: string;
  emptyMessage?: string;
};

/** Selectable summary list for recently generated app-access packages. */
export const AppAccessPackageList = ({
  packages,
  selectedId,
  isLoading = false,
  onSelect,
  className,
  title = "Recent setups",
  emptyMessage = "No app setups have been created yet.",
}: AppAccessPackageListProps) => (
  <section className={[appAccessCardClass, className].filter(Boolean).join(" ")} aria-labelledby="app-access-packages-heading">
    <h2 id="app-access-packages-heading" className="font-semibold text-[var(--ots-secondary)]">{title}</h2>
    <div className="mt-3 space-y-2">
      {isLoading && <p className="text-sm text-[var(--ots-text-muted)]">Loading…</p>}
      {!isLoading && packages.length === 0 && <p className="text-sm text-[var(--ots-text-muted)]">{emptyMessage}</p>}
      {packages.map((item) => {
        const deliveryStatus = getAppAccessPackageDeliveryStatus(item);
        return <button
          type="button"
          key={item.id}
          onClick={() => onSelect(item)}
          aria-pressed={selectedId === item.id}
          className={`w-full rounded-xl border p-3 text-left transition-colors ${selectedId === item.id ? "border-[var(--ots-primary)] bg-[var(--ots-primary-container)]" : "border-[var(--ots-outline-variant)] bg-[var(--ots-field)] hover:bg-[var(--ots-background)]"}`}>
          <span className="flex items-center gap-3">
            <span className="flex -space-x-2">
              {item.targets.slice(0, 3).map((target) => {
                const app = getAppAccessApp(target.app);
                return app ? <img key={target.id} src={app.logo} alt="" className="size-8 rounded-lg border-2 border-[var(--ots-field)]"/> : null;
              })}
            </span>
            <span className="min-w-0 flex-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate font-semibold">{item.name}</span>
                <StatusPill tone={deliveryStatus.tone}>{deliveryStatus.label}</StatusPill>
              </span>
              <span className="mt-1 block text-xs text-[var(--ots-text-muted)]">
                {item.managed ? "Managed reusable onboarding" : item.mode === "ONE_TIME_CREDENTIAL" ? "One-time app setup" : "Reusable onboarding"} · <OtsDateTime value={item.createDttm} displayTime={false} />
                {item.defaultRegionName ? ` · ${item.defaultRegionName}` : ""}
              </span>
              <span className="mt-1 block truncate text-xs text-[var(--ots-text-muted)]" title={getAppAccessIdentitySummary(item.targets)}>
                App Identity: {getAppAccessIdentitySummary(item.targets)}
              </span>
            </span>
          </span>
        </button>;
      })}
    </div>
  </section>
);
